import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import socket from '../utils/socket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BUG FIX (Latest-3 #2) — ONE shared notification-tracking mechanism for
// EVERY dashboard AND chat page, across BOTH Admin and Handler roles. All
// notifications (dashboard room-poll + chat socket) funnel through
// showNotification(), which is now the single dedup authority:
//   • persistent per-message dedup (msg id) → a given message notifies once
//     for the whole session, no matter which page (dashboard OR chat) sees it;
//   • per-room/group short-window dedup → covers dashboard events that carry no
//     message id and guards against near-simultaneous double fires.
// This replaces the previously independent per-page Set/useRef trackers as the
// notification gate.
const _notifiedMsgIds = new Set();        // session-persistent message ids
const _recentNotifyKeys = new Map();      // roomId/groupId -> last-fired ms
const _NOTIFY_DEDUP_MS = 6000;
const _shouldNotify = ({ messageId, key } = {}) => {
  const now = Date.now();
  // 1) Persistent per-message dedup (shared across dashboard + chat + session).
  if (messageId) {
    if (_notifiedMsgIds.has(messageId)) return false;
    _notifiedMsgIds.add(messageId);
    if (_notifiedMsgIds.size > 2000) {
      // keep the set bounded — drop oldest ~500 entries
      let i = 0;
      for (const id of _notifiedMsgIds) { _notifiedMsgIds.delete(id); if (++i >= 500) break; }
    }
  }
  // 2) Per-room/group short-window dedup.
  if (key) {
    const last = _recentNotifyKeys.get(key) || 0;
    if (now - last < _NOTIFY_DEDUP_MS) return false;
    _recentNotifyKeys.set(key, now);
    if (_recentNotifyKeys.size > 200) {
      for (const [k, t] of _recentNotifyKeys) if (now - t > _NOTIFY_DEDUP_MS) _recentNotifyKeys.delete(k);
    }
  }
  return true;
};

// Check if Notification API is available
const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

// Get initial permission safely
const getInitialPermission = () => {
  if (isNotificationSupported()) {
    return Notification.permission;
  }
  return 'denied'; // Default to denied if not supported
};

export const useWebNotification = () => {
  const [permission, setPermission] = useState(getInitialPermission);
  const [soundUrl, setSoundUrl] = useState(null);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('dws_user') || '{}');

  useEffect(() => {
    loadNotificationSound();
    registerServiceWorker();

    // Point 5 (New Points) — live-refresh the notification tune. Fetch once
    // on mount, then again whenever the tab regains focus and every 60s, so a
    // tune changed in Settings updates already-open sessions without a reload.
    const refreshTune = () => { loadNotificationSound(); };
    const onVis = () => { if (document.visibilityState === 'visible') refreshTune(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refreshTune);
    const tuneInterval = setInterval(refreshTune, 60000);
    // Instant apply — admin changes the tune → server broadcasts, every open
    // admin/handler session refreshes immediately (no reload, no 60s wait).
    const onTuneUpdated = () => { setSoundUrl(null); loadNotificationSound(); };
    socket.on('notification_tune_updated', onTuneUpdated);

    if (isNotificationSupported()) {
      const hasAskedPermission = localStorage.getItem('notificationPermissionAsked');
      if (Notification.permission === 'default' && !hasAskedPermission) {
        requestPermission();
        localStorage.setItem('notificationPermissionAsked', 'true');
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refreshTune);
      clearInterval(tuneInterval);
      socket.off('notification_tune_updated', onTuneUpdated);
    };
  }, []);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  };

  const _abToB64Url = (buf) => {
    try {
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch { return null; }
  };

  const subscribeToPush = async (registration) => {
    try {
      if (!('PushManager' in window)) return;
      if (Notification.permission !== 'granted') return;
      let userId;
      try { userId = JSON.parse(localStorage.getItem('dws_user') || '{}').id; } catch { userId = null; }
      if (!userId) return;
      const keyRes = await axios.get(`${API}/push/vapid-public-key`);
      const publicKey = keyRes.data?.public_key;
      if (!publicKey) return;
      let sub = await registration.pushManager.getSubscription();
      // Critical: if an existing browser subscription was created with a
      // DIFFERENT VAPID key (e.g. an older key before it was persisted), the
      // push service rejects every send with 403 and the backend prunes it —
      // so admins/handlers silently never get push. Detect the mismatch and
      // re-subscribe fresh with the current server key.
      if (sub) {
        const existingKey = sub.options && sub.options.applicationServerKey
          ? _abToB64Url(sub.options.applicationServerKey)
          : null;
        if (existingKey !== publicKey) {
          try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
          sub = null;
        }
      }
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      await axios.post(`${API}/push/subscribe`, { user_id: userId, subscription: sub.toJSON() });
    } catch (e) {
      // Best-effort: push subscription is a bonus on top of in-app notifications.
    }
  };

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        setServiceWorkerRegistration(registration);
        await navigator.serviceWorker.ready;
        // Point 3 (New Points) — subscribe to true Web Push so notifications
        // arrive even when the app/PWA is fully closed. Best-effort.
        subscribeToPush(registration);
        // Poll for SW updates whenever the tab comes back into focus — this
        // is what makes the "old UI on PWA reopen" bug go away without any
        // manual refresh. Combined with the network-first fetch handler +
        // controllerchange auto-reload in index.js, users always see the
        // fresh deploy within seconds of resuming the app.
        const checkForUpdate = () => {
          // registration.update() returns a PROMISE — when offline the SW
          // script fetch fails and rejects. A sync try/catch can't catch a
          // promise rejection, so we MUST .catch() it, otherwise it surfaces
          // as an "Uncaught runtime error" (CRA dev overlay) on offline open.
          try {
            const p = registration.update();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } catch (_) { /* ignore */ }
        };
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') checkForUpdate();
        });
        window.addEventListener('focus', checkForUpdate);
        // Also check right after registration so a stale bundle on a
        // freshly-opened PWA reloads immediately.
        setTimeout(checkForUpdate, 2000);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  };

  const loadNotificationSound = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.notification_tune_url) {
        const url = `${BACKEND_URL}${response.data.notification_tune_url}`;
        setSoundUrl(url);
      }
    } catch (error) {
      console.error('Failed to load notification sound:', error);
    }
  };

  const requestPermission = async () => {
    if (isNotificationSupported()) {
      try {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        localStorage.setItem('notificationPermissionAsked', 'true');
        // Subscribe to true Web Push RIGHT NOW (same user-gesture/session) on
        // grant. Critical for mobile/PWA where the browser only prompts after a
        // user tap — the old flow subscribed on mount before permission was
        // granted, so customers never actually got a push subscription.
        if (perm === 'granted') {
          try {
            const reg = await navigator.serviceWorker.ready;
            await subscribeToPush(reg);
          } catch (_) { /* best-effort */ }
        }
        return perm;
      } catch (error) {
        return 'denied';
      }
    }
    return 'denied';
  };

  const playFallbackBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      // silently fail
    }
  };

  const playNotificationSound = async () => {
    // Check if customer has set a custom tune (stored in localStorage)
    const customerTune = localStorage.getItem('dws_customer_tune');
    
    // None = Silent mode
    if (customerTune === 'none') return;
    
    if (customerTune && customerTune !== 'default' && customerTune !== 'none') {
      if (customerTune === 'custom') {
        const customUrl = localStorage.getItem('dws_customer_tune_url');
        if (customUrl) {
          try {
            const audio = new Audio(customUrl);
            audio.volume = 0.5;
            await audio.play();
            return;
          } catch(e) { /* fallback */ }
        }
      }
      const freqMap = { tone1: 523, tone2: 392, tone3: 587, tone4: 330 };
      const freq = freqMap[customerTune];
      if (freq) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = customerTune === 'tone3' ? 'square' : customerTune === 'tone2' ? 'sine' : 'triangle';
          gain.gain.value = 0.3;
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
          osc.start();
          osc.stop(ctx.currentTime + 0.8);
          return;
        } catch(e) { /* fallback */ }
      }
    }
    
    // Use admin-configured sound
    let urlToPlay = soundUrl;
    if (!urlToPlay) {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data.notification_tune_url) {
          urlToPlay = `${BACKEND_URL}${response.data.notification_tune_url}`;
          setSoundUrl(urlToPlay);
        }
      } catch (err) { /* ignore */ }
    }
    
    if (urlToPlay) {
      try {
        let fullSoundUrl = urlToPlay;
        if (!urlToPlay.startsWith('http')) {
          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
          if (urlToPlay.startsWith('/media/')) {
            fullSoundUrl = `${baseUrl}/api${urlToPlay}`;
          } else if (urlToPlay.startsWith('/api/')) {
            fullSoundUrl = `${baseUrl}${urlToPlay}`;
          } else {
            fullSoundUrl = `${baseUrl}/api/media/${urlToPlay.split('/').pop()}`;
          }
        }
        
        const audio = new Audio(fullSoundUrl);
        audio.volume = 0.7;
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => playFallbackBeep());
        }
      } catch (error) {
        playFallbackBeep();
      }
    } else {
      playFallbackBeep();
    }
  };

  const showNotification = async ({ title, body, icon, tag, data, silent }) => {
    // BUG FIX (Latest-3 #2) — single shared dedup authority. Skip if this exact
    // message was already notified this session (msg id, shared across dashboard
    // + chat) OR the same room/group fired moments ago from another page.
    const dedupKey = (data && (data.roomId || data.groupId)) || tag || title;
    const messageId = data && data.messageId;
    if (!_shouldNotify({ messageId, key: dedupKey })) return;

    // Push notification toggle only mutes SOUND, badges and visual notifications remain
    
    if (!isNotificationSupported() || permission !== 'granted') {
      if (!silent) await playNotificationSound();
      return;
    }

    try {
      if (!silent) await playNotificationSound();

      // Ensure the notification payload always carries the recipient's role so
      // both the foreground click handler and the service worker can build the
      // correct chat route for admin / handler / customer.
      const dataWithRole = { ...(data || {}), role: currentUser?.role };

      if (serviceWorkerRegistration && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_NOTIFICATION',
          payload: { title, body, tag, data: dataWithRole }
        });
      } else if (isNotificationSupported()) {
        const notificationData = dataWithRole;
        const notification = new Notification(title, {
          body: body,
          tag: tag || 'dws-vault-chat',
          requireInteraction: false,
          silent: true
        });

        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          if (notificationData && notificationData.roomId) {
            const role = currentUser.role || '';
            let path = '';
            if (role === 'super_admin' || role === 'admin') path = `/admin-chat/${notificationData.roomId}`;
            else if (role.endsWith('_handler') || role === 'handler') path = `/handler-chat/${notificationData.roomId}`;
            else if (role === 'customer') path = `/customer-chat?type=${notificationData.chatType || ''}`;
            if (path) window.location.href = path;
          }
          notification.close();
        };

        setTimeout(() => notification.close(), 5000);
      }
    } catch (error) {
      if (!silent) await playNotificationSound();
    }
  };

  const updateAppBadge = async (count) => {
    try {
      if ('setAppBadge' in navigator) {
        if (count > 0) await navigator.setAppBadge(count);
        else await navigator.clearAppBadge();
      }
    } catch (err) { /* silently fail */ }
  };

  return {
    permission,
    requestPermission,
    showNotification,
    playNotificationSound,
    updateAppBadge
  };
};
